import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SalesChart from "@/components/SalesChart";
import {
  PhoneCall,
  Users,
  Clock,
  ClipboardList,
  Radio,
  ArrowUpRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

function Metric({ icon: Icon, label, value, unit, accent, tid }) {
  return (
    <div
      className="border-r border-b border-slate-200 p-6 bg-white"
      data-testid={tid}
    >
      <div className="flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
          {label}
        </div>
        <Icon className={`w-4 h-4 ${accent || "text-[#0055FF]"}`} />
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-heading text-3xl font-bold">{value}</div>
        {unit && <div className="mono text-xs text-slate-400">{unit}</div>}
      </div>
    </div>
  );
}

export default function Overview() {
  const [summary, setSummary] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([
        api.get("/analytics/summary"),
        api.get("/calls"),
      ]);
      setSummary(s.data);
      setRecentCalls(c.data.slice(0, 6));
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-overview">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Dashboard
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">Overview</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Live snapshot of your calls, team and pending work.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 border-l border-t border-slate-200">
        <Metric
          icon={PhoneCall}
          label="Total Calls"
          value={summary?.total_calls ?? "—"}
          tid="metric-total-calls"
        />
        <Metric
          icon={Clock}
          label="Talk Time"
          value={summary?.total_talk_minutes ?? "—"}
          unit="min"
          accent="text-[#FF5000]"
          tid="metric-talk-time"
        />
        <Metric
          icon={Users}
          label="Employees"
          value={summary?.total_employees ?? "—"}
          tid="metric-employees"
        />
        <Metric
          icon={ClipboardList}
          label="Pending Tasks"
          value={summary?.pending_tasks ?? "—"}
          accent="text-[#FF5000]"
          tid="metric-pending-tasks"
        />
      </div>

      <div className="mt-6">
        <SalesChart days={14} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <Card className="lg:col-span-2 border-slate-200 shadow-none">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">Call volume</CardTitle>
              <div className="mono text-xs text-slate-400">last 7 days</div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={summary?.series || []}>
                <CartesianGrid stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}
                />
                <YAxis
                  stroke="#94a3b8"
                  tick={{ fontFamily: "IBM Plex Mono", fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    fontFamily: "Manrope",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#0055FF"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#0055FF" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg">
                Recent calls
              </CardTitle>
              <Radio className="w-4 h-4 text-[#0055FF]" />
            </div>
          </CardHeader>
          <CardContent className="p-0 divide-y">
            {recentCalls.length === 0 && (
              <div className="p-6 text-sm text-slate-500">No calls yet.</div>
            )}
            {recentCalls.map((c) => (
              <div
                key={c.id}
                className="p-4 flex items-center gap-3"
                data-testid={`recent-call-${c.id}`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    c.direction === "inbound" ? "bg-[#0055FF]" : "bg-[#FF5000]"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {c.customer_name}
                  </div>
                  <div className="mono text-[11px] text-slate-500 truncate">
                    {c.customer_phone} · IVR {c.ivr_path || "—"}
                  </div>
                </div>
                <div className="mono text-xs text-slate-500 tabular-nums">
                  {Math.floor(c.duration_sec / 60)}:
                  {String(c.duration_sec % 60).padStart(2, "0")}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <a
          href="/dashboard/ivr"
          className="inline-flex items-center gap-2 mono text-sm text-[#0055FF] hover:underline"
          data-testid="link-open-ivr"
        >
          Open IVR builder <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
